import { useEffect, useState } from 'react';
import { Courier, CourierService, PincodeZone } from '../types/pincode';
import { pincodeService } from '../services/pincode';

interface UsePincodeResult {
  couriers: Courier[];
  services: CourierService[];
  zones: PincodeZone[];
  loading: boolean;
  error: string | null;
  fetchCouriers: () => void;
  fetchServices: (courierId: string) => void;
  fetchZones: (userId: string, courierId: string, pickup: string, delivery: string) => void;
}

export const usePincode = (): UsePincodeResult => {
  const [couriers, setCouriers] = useState<Courier[]>([]);
  const [services, setServices] = useState<CourierService[]>([]);
  const [zones, setZones] = useState<PincodeZone[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCouriers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pincodeService.getAllCouriers();
      setCouriers(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch couriers');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async (courierId: string) => {
    if (!courierId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await pincodeService.getCourierServices(courierId);
      setServices(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };

  const fetchZones = async (
    userId: string,
    courierId: string,
    pickup: string,
    delivery: string
  ) => {
    if (!userId || !courierId || !pickup || !delivery) return;
    setLoading(true);
    setError(null);
    try {
      const data = await pincodeService.getPincodeZones(
        userId,
        courierId,
        pickup,
        delivery
      );
      setZones(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch zones');
    } finally {
      setLoading(false);
    }
  };

  return {
    couriers,
    services,
    zones,
    loading,
    error,
    fetchCouriers,
    fetchServices,
    fetchZones,
  };
};
